// @flow

import React, { useState } from 'react';
import styled from 'styled-components';
import { colors, fonts } from 'shared/styleguide';
import { i18n } from 'shared/utils';
import { getCurrencySymbol } from 'shared/utils/currency';
import Panel from 'shared/components/common/Panel';
import moment from 'moment';

type CostHistoryData = {
  calculatedMachineCost: ?number,
  calculatedMaterialCost: ?number,
  totalCalculatedCost: ?number,
  targetCost: ?number,
  snapshotDate: string,
};

type Props = {
  costHistory: Array<CostHistoryData>,
  currentCost: number,
  targetCost: number,
};

const ChartWrapper = styled.div`
  position: relative;
  height: 400px;
  padding: 24px 16px 48px 56px;
`;

const SVGChart = styled.svg`
  width: 100%;
  height: 100%;
`;

const YAxisLabel = styled.div`
  position: absolute;
  left: 0;
  top: 50%;
  transform: rotate(-90deg) translateX(-50%);
  transform-origin: left center;
  ${fonts.smallRegular};
  color: ${colors.battleshipGrey};
  white-space: nowrap;
`;

const XAxisLabels = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 56px 0 56px;
`;

const XAxisLabel = styled.div`
  ${fonts.smallRegular};
  color: ${colors.battleshipGrey};
  text-align: center;
`;

const Legend = styled.div`
  display: flex;
  justify-content: center;
  gap: 32px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid ${colors.paleGrey};
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LegendLine = styled.div`
  width: 32px;
  height: 3px;
  background-color: ${p => p.color};
  border-radius: 2px;
`;

const LegendText = styled.div`
  ${fonts.bodyRegular};
  color: ${colors.charcoalGrey};
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const StatCard = styled.div`
  padding: 16px;
  border-radius: 8px;
  background-color: ${colors.paleGreyThree};
  border: 1px solid ${colors.paleGrey};
`;

const StatLabel = styled.div`
  ${fonts.smallRegular};
  color: ${colors.battleshipGrey};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`;

const StatValue = styled.div`
  ${fonts.largeBold};
  color: ${colors.charcoalGrey};
`;

const StatChange = styled.div`
  ${fonts.smallRegular};
  color: ${p => (p.positive ? colors.jungleGreen : colors.cherry)};
  margin-top: 4px;
`;

const Tooltip = styled.div`
  position: absolute;
  background-color: ${colors.charcoalGrey};
  color: ${colors.white};
  padding: 12px 16px;
  border-radius: 6px;
  ${fonts.smallRegular};
  pointer-events: none;
  white-space: nowrap;
  z-index: 10;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  
  &::after {
    content: '';
    position: absolute;
    bottom: -6px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 6px solid ${colors.charcoalGrey};
  }
`;

const TooltipLabel = styled.div`
  margin-bottom: 4px;
  opacity: 0.8;
`;

const TooltipValue = styled.div`
  ${fonts.bodyBold};
`;

const BOMCostTrendChart = ({ costHistory, currentCost, targetCost }: Props) => {
  const [tooltip, setTooltip] = useState(null);
  
  // Transform cost history data for chart
  const data = costHistory.map(h => ({
    date: h.snapshotDate,
    actualCost: h.totalCalculatedCost || 0,
    targetCost: h.targetCost || targetCost,
  }));

  // If no history data, show message
  if (data.length === 0) {
    return (
      <Panel 
        title={i18n.t('Cost Trend Analysis')}
        showHeaderBorder
      >
        <div style={{ padding: '48px', textAlign: 'center', color: colors.battleshipGrey }}>
          <div style={{ fontSize: '16px', marginBottom: '8px' }}>
            {i18n.t('No cost history available yet')}
          </div>
          <div style={{ fontSize: '14px' }}>
            {i18n.t('Cost trends will appear here as data is collected over time')}
          </div>
        </div>
      </Panel>
    );
  }

  // Calculate chart dimensions
  const width = 100; // percentage
  const height = 100; // percentage
  const padding = 5;

  // Find min and max values for scaling
  const allCosts = data.flatMap(d => [d.actualCost, d.targetCost]);
  const minCost = Math.min(...allCosts) * 0.95;
  const maxCost = Math.max(...allCosts) * 1.05;

  // Scale functions
  const scaleX = (index: number) => {
    return (index / (data.length - 1)) * (width - 2 * padding) + padding;
  };

  const scaleY = (value: number) => {
    const normalized = (value - minCost) / (maxCost - minCost);
    return height - padding - normalized * (height - 2 * padding);
  };

  // Create path for actual costs
  const actualPath = data
    .map((d, i) => {
      const x = scaleX(i);
      const y = scaleY(d.actualCost);
      return i === 0 ? `M ${x} ${y}` : `L ${x} ${y}`;
    })
    .join(' ');

  // Create path for target costs
  const targetPath = data
    .map((d, i) => {
      const x = scaleX(i);
      const y = scaleY(d.targetCost);
      return i === 0 ? `M ${x} ${y}` : `L ${x} ${y}`;
    })
    .join(' ');

  // Calculate statistics
  const currentActualCost = data[data.length - 1].actualCost;
  const previousCost = data.length > 1 ? data[data.length - 2].actualCost : currentActualCost;
  const costChange = currentActualCost - previousCost;
  const costChangePercent = previousCost > 0 ? (costChange / previousCost) * 100 : 0;
  
  const avgCost = data.reduce((sum, d) => sum + d.actualCost, 0) / data.length;

  const handleMouseMove = (e: MouseEvent, d: Object, index: number) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top - 60,
      data: d,
    });
  };

  const handleMouseLeave = () => {
    setTooltip(null);
  };

  return (
    <Panel 
      title={i18n.t('Cost Trend Analysis')}
      showHeaderBorder
    >
      <StatsGrid>
        <StatCard>
          <StatLabel>{i18n.t('Current Cost')}</StatLabel>
          <StatValue>{getCurrencySymbol()}{currentActualCost.toFixed(2)}</StatValue>
          <StatChange positive={costChange <= 0}>
            {costChange >= 0 ? '+' : ''}{getCurrencySymbol()}{costChange.toFixed(2)} (
            {costChangePercent >= 0 ? '+' : ''}
            {costChangePercent.toFixed(1)}%)
          </StatChange>
        </StatCard>

        <StatCard>
          <StatLabel>{i18n.t('Average Cost')}</StatLabel>
          <StatValue>{getCurrencySymbol()}{avgCost.toFixed(2)}</StatValue>
          <StatChange positive={avgCost <= targetCost}>
            {avgCost > targetCost ? 'Above' : 'Below'} target
          </StatChange>
        </StatCard>

        <StatCard>
          <StatLabel>{i18n.t('Target Cost')}</StatLabel>
          <StatValue>{getCurrencySymbol()}{targetCost.toFixed(2)}</StatValue>
          <StatChange positive={currentActualCost <= targetCost}>
            {currentActualCost > targetCost ? 'Over' : 'Under'} by $
            {Math.abs(currentActualCost - targetCost).toFixed(2)}
          </StatChange>
        </StatCard>

        <StatCard>
          <StatLabel>{i18n.t('Cost Variance')}</StatLabel>
          <StatValue>
            {targetCost > 0 ? ((currentActualCost - targetCost) / targetCost * 100).toFixed(1) : '0.0'}%
          </StatValue>
          <StatChange positive={(currentActualCost - targetCost) <= 0}>
            {currentActualCost > targetCost ? 'Above' : 'Below'} target
          </StatChange>
        </StatCard>
      </StatsGrid>

      <ChartWrapper>
        <YAxisLabel>{i18n.t('Cost ($)')}</YAxisLabel>
        <SVGChart viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
          {/* Grid lines */}
          {[0, 25, 50, 75, 100].map(y => (
            <line
              key={y}
              x1={padding}
              y1={y}
              x2={width - padding}
              y2={y}
              stroke={colors.paleGrey}
              strokeWidth="0.3"
            />
          ))}

          {/* Target line (dashed) */}
          <path
            d={targetPath}
            fill="none"
            stroke={colors.battleshipGrey}
            strokeWidth="0.5"
            strokeDasharray="2,2"
          />

          {/* Actual cost line */}
          <path
            d={actualPath}
            fill="none"
            stroke={colors.azul}
            strokeWidth="0.8"
          />

          {/* Data points */}
          {data.map((d, i) => (
            <g key={i}>
              <circle
                cx={scaleX(i)}
                cy={scaleY(d.actualCost)}
                r="1.5"
                fill={colors.azul}
                stroke={colors.white}
                strokeWidth="0.5"
                style={{ cursor: 'pointer' }}
                onMouseMove={(e) => handleMouseMove(e, d, i)}
                onMouseLeave={handleMouseLeave}
              />
            </g>
          ))}
        </SVGChart>

        {tooltip && (
          <Tooltip style={{ left: tooltip.x, top: tooltip.y }}>
            <TooltipLabel>
              {moment(tooltip.data.date).format('MMM DD, YYYY')}
            </TooltipLabel>
            <TooltipValue>
              Actual: {getCurrencySymbol()}{tooltip.data.actualCost.toFixed(2)}
            </TooltipValue>
            <TooltipValue>
              Target: {getCurrencySymbol()}{tooltip.data.targetCost.toFixed(2)}
            </TooltipValue>
          </Tooltip>
        )}
      </ChartWrapper>

      <XAxisLabels>
        {[0, Math.floor(data.length / 3), Math.floor(2 * data.length / 3), data.length - 1].map(i => (
          <XAxisLabel key={i}>
            {moment(data[i].date).format('MMM DD')}
          </XAxisLabel>
        ))}
      </XAxisLabels>

      <Legend>
        <LegendItem>
          <LegendLine color={colors.azul} />
          <LegendText>{i18n.t('Actual Cost')}</LegendText>
        </LegendItem>
        <LegendItem>
          <LegendLine color={colors.battleshipGrey} style={{ backgroundImage: 'repeating-linear-gradient(to right, transparent, transparent 4px, ' + colors.battleshipGrey + ' 4px, ' + colors.battleshipGrey + ' 8px)' }} />
          <LegendText>{i18n.t('Target Cost')}</LegendText>
        </LegendItem>
      </Legend>
    </Panel>
  );
};

export default BOMCostTrendChart;
